/**
 * Periodontograma (Especialidades → Periodoncia): registro de los seis sitios de cada diente con profundidad de sondaje, recesión,
 * sangrado, placa y supuración, más movilidad y furca. Se guarda cada vez como un registro nuevo e inalterable y se compara con el anterior.
 */
import { useEffect, useMemo, useState } from "react";
import { api, puede, type Paciente } from "../api.ts";
import {
  DIENTES_SUPERIORES, DIENTES_INFERIORES, periodontogramaVacio, limpiarPeriodontograma, resumenPeriodontograma, textoResumenPerio, compararPerio, problemasPeriodontograma, tieneFurca, nic,
  type DatosPerio, type SitioPerio, type ResumenPerio,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";
import { hoy } from "./comun.tsx";

interface Registro { id: string; fecha: string; datos: DatosPerio; resumen: ResumenPerio; observaciones: string; profesional: string }

const fechaLarga = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" });
const signo = (n: number | null, unidad: string) => (n === null ? "" : n === 0 ? `sin cambio en ${unidad}` : `${n < 0 ? "▼" : "▲"} ${n > 0 ? "+" : "−"}${Math.abs(n)} ${unidad}`);

/** Casilla numérica de una medida: acepta vacío (sin medir) y, si se permite, el signo menos. Enter pasa a la siguiente. */
function Num({ valor, alCambiar, min, max, etiqueta, bloqueado, clase }: { valor: number | null; alCambiar: (n: number | null) => void; min: number; max: number; etiqueta: string; bloqueado: boolean; clase?: string }) {
  const leer = (t: string): number | null => { const n = Number(t); return t.trim() === "" || !/^-?\d{1,2}$/.test(t.trim()) || n < min || n > max ? null : n; };
  const [t, setT] = useState(valor === null ? "" : String(valor));
  useEffect(() => { if (leer(t) !== valor) setT(valor === null ? "" : String(valor)); }, [valor]);
  return (
    <input className={`perio-n ${clase ?? ""}`} inputMode="numeric" maxLength={min < 0 ? 3 : 2} aria-label={etiqueta} value={t} disabled={bloqueado}
      onChange={(e) => { const x = e.target.value; if (x === "" || /^-?\d{0,2}$/.test(x)) { setT(x); alCambiar(leer(x)); } }}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const todas = [...(e.currentTarget.closest(".perio-tabla")?.querySelectorAll<HTMLInputElement>("input.perio-n:not(:disabled)") ?? [])];
        todas[todas.indexOf(e.currentTarget) + 1]?.focus();
      }} />
  );
}

const Marca = ({ activa, alCambiar, etiqueta, bloqueado, clase }: { activa: boolean; alCambiar: () => void; etiqueta: string; bloqueado: boolean; clase: string }) => (
  <button type="button" className={`perio-m ${clase}${activa ? " on" : ""}`} aria-pressed={activa} aria-label={etiqueta} disabled={bloqueado} onClick={alCambiar}>{activa ? "●" : ""}</button>
);

const NOMBRE_SITIO = ["mesial", "central", "distal"];

function Arcada({ dientes, datos, cambiar, bloqueado, inferior }: { dientes: readonly number[]; datos: DatosPerio; cambiar: (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => void; bloqueado: boolean; inferior: boolean }) {
  const caras: ["v" | "l", string][] = inferior ? [["l", "Lingual"], ["v", "Vestibular"]] : [["v", "Vestibular"], ["l", "Palatino"]];
  const sitio = (n: number, cara: "v" | "l", i: number, f: (s: SitioPerio) => SitioPerio) => cambiar(n, (d) => ({ ...d, [cara]: d[cara].map((s, j) => (j === i ? f(s) : s)) }));
  return (
    <div className="perio-scroll">
      <table className="perio-tabla">
        <thead>
          <tr><th scope="row" className="perio-et">Diente</th>
            {dientes.map((n) => (
              <th key={n} colSpan={3} className={datos[n].ausente ? "perio-aus" : ""}>
                <button type="button" className="perio-dte" disabled={bloqueado} aria-pressed={datos[n].ausente} title={datos[n].ausente ? "Ausente: toca para marcar presente" : "Toca para marcar ausente"} onClick={() => cambiar(n, (d) => ({ ...d, ausente: !d.ausente }))}>{n}</button>
              </th>
            ))}</tr>
          <tr><th scope="row" className="perio-et">Movilidad</th>
            {dientes.map((n) => (
              <td key={n} colSpan={3}>{!datos[n].ausente && <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Movilidad del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, mov: (d.mov + 1) % 4 }))}>{datos[n].mov || "·"}</button>}</td>
            ))}</tr>
          <tr><th scope="row" className="perio-et">Furca</th>
            {dientes.map((n) => (
              <td key={n} colSpan={3}>{!datos[n].ausente && tieneFurca(n) && <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Furca del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, furca: (d.furca + 1) % 4 }))}>{datos[n].furca || "·"}</button>}</td>
            ))}</tr>
        </thead>
        {caras.map(([cara, nombre]) => (
          <tbody key={cara}>
            <tr className="perio-cara"><th colSpan={1 + dientes.length * 3}>{nombre}</th></tr>
            <tr><th scope="row" className="perio-et">Placa</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => <td key={`${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && <Marca clase="placa" activa={datos[n][cara][i].placa} bloqueado={bloqueado} etiqueta={`Placa ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={() => sitio(n, cara, i, (s) => ({ ...s, placa: !s.placa }))} />}</td>))}</tr>
            <tr><th scope="row" className="perio-et">Sangrado</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => <td key={`${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && <Marca clase="sang" activa={datos[n][cara][i].sang} bloqueado={bloqueado} etiqueta={`Sangrado ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={() => sitio(n, cara, i, (s) => ({ ...s, sang: !s.sang }))} />}</td>))}</tr>
            <tr><th scope="row" className="perio-et">Supuración</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => <td key={`${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && <Marca clase="sup" activa={datos[n][cara][i].sup} bloqueado={bloqueado} etiqueta={`Supuración ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={() => sitio(n, cara, i, (s) => ({ ...s, sup: !s.sup }))} />}</td>))}</tr>
            <tr><th scope="row" className="perio-et" title="Recesión en mm: positivo = el margen está por debajo del límite amelocementario; negativo = por encima">Recesión</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => <td key={`${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && <Num valor={datos[n][cara][i].rec} min={-5} max={15} bloqueado={bloqueado} etiqueta={`Recesión ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (s) => ({ ...s, rec: x }))} />}</td>))}</tr>
            <tr><th scope="row" className="perio-et">Sondaje</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => { const s = datos[n][cara][i]; return <td key={`${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && <Num valor={s.pd} min={0} max={15} bloqueado={bloqueado} clase={s.pd !== null && s.pd >= 6 ? "grave" : s.pd !== null && s.pd >= 4 ? "alto" : ""} etiqueta={`Sondaje ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (y) => ({ ...y, pd: x }))} />}</td>; }))}</tr>
            <tr><th scope="row" className="perio-et" title="Nivel de inserción clínica = sondaje + recesión">NIC</th>{dientes.flatMap((n) => [0, 1, 2].map((i) => <td key={`${n}${i}`} className={`perio-nic${datos[n].ausente ? " perio-aus" : ""}`}>{!datos[n].ausente && (nic(datos[n][cara][i]) ?? "")}</td>))}</tr>
          </tbody>
        ))}
      </table>
    </div>
  );
}

function Resumen({ r }: { r: ResumenPerio }) {
  const casilla = (t: string, v: string, grave = false) => <span className={`chip ${grave ? "rojo" : ""}`}>{t}: <b>{v}</b></span>;
  return (
    <div className="fila" style={{ flexWrap: "wrap", gap: 6 }} aria-live="polite">
      {casilla("Dientes", String(r.dientes))}
      {casilla("Sitios medidos", String(r.sitios))}
      {casilla("Sondaje medio", r.pdMedia === null ? "—" : `${r.pdMedia} mm`)}
      {casilla("NIC medio", r.nicMedia === null ? "—" : `${r.nicMedia} mm`)}
      {casilla("Sitios ≥ 4 mm", String(r.sitios4), r.sitios4 > 0)}
      {casilla("Sitios ≥ 6 mm", String(r.sitios6), r.sitios6 > 0)}
      {casilla("Sangrado", r.pctSangrado === null ? "—" : `${r.pctSangrado} %`, (r.pctSangrado ?? 0) > 30)}
      {casilla("Placa", r.pctPlaca === null ? "—" : `${r.pctPlaca} %`, (r.pctPlaca ?? 0) > 30)}
      {r.supuracion > 0 && casilla("Supuración", `${r.supuracion} sitios`, true)}
      {r.conMovilidad > 0 && casilla("Con movilidad", String(r.conMovilidad))}
      {r.conFurca > 0 && casilla("Con furca", String(r.conFurca))}
    </div>
  );
}

export function Periodontograma({ paciente }: { paciente: Paciente }) {
  const [lista, setLista] = useState<Registro[] | null>(null);
  const [datos, setDatos] = useState<DatosPerio | null>(null);
  const [viendo, setViendo] = useState<Registro | null>(null);
  const [fecha, setFecha] = useState(hoy());
  const [obs, setObs] = useState("");
  const a = useAccion();
  const url = `/api/pacientes/${paciente.id}/periodontogramas`;
  useEffect(() => { setLista(null); setDatos(null); setViendo(null); api<{ periodontogramas: Registro[] }>("GET", url).then((r) => setLista(r.periodontogramas)).catch(a.setError); }, [paciente.id]);

  const limpio = useMemo(() => (datos ? limpiarPeriodontograma(datos) : null), [datos]);
  const resumen = useMemo(() => (limpio ? resumenPeriodontograma(limpio) : null), [limpio]);
  const problemas = limpio ? problemasPeriodontograma(limpio) : [];
  const editable = !!datos && !viendo;

  const nuevo = () => {
    // los dientes que ya faltaban en el último registro se dejan como ausentes
    const base = periodontogramaVacio();
    const ultimo = lista?.[0];
    if (ultimo) for (const n of Object.keys(base)) if (ultimo.datos[n]?.ausente) base[n].ausente = true;
    setDatos(base); setViendo(null); setObs(""); setFecha(hoy());
  };
  const cambiar = (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => setDatos((d) => (d ? { ...d, [String(n)]: f(d[String(n)]) } : d));
  const guardar = async () => {
    const r = await a.correr(() => api("POST", url, { fecha, observaciones: obs, datos: limpio }));
    if (!r) return;
    const x = await api<{ periodontogramas: Registro[] }>("GET", url);
    setLista(x.periodontogramas); setDatos(null);
  };

  if (!lista) return <AvisoError error={a.error} />;
  const mostrado = viendo ? viendo.datos : datos;
  return (
    <Seccion titulo="Periodontograma" acciones={puede("clinico:escribir") && !datos && <button className="btn chico pri" onClick={nuevo}>Nuevo periodontograma</button>}>
      {!mostrado && lista.length === 0 && <p className="muted" style={{ margin: 0 }}>Sin periodontogramas registrados. Cada uno se guarda como un registro nuevo para poder comparar la evolución.</p>}
      {mostrado && (
        <>
          <div className="fila" style={{ flexWrap: "wrap", alignItems: "end" }}>
            {viendo
              ? <b>Periodontograma del {fechaLarga(viendo.fecha)} · {viendo.profesional}</b>
              : <label className="campo" style={{ margin: 0 }}>Fecha<FechaInput max={hoy()} value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>}
            {viendo && <button className="btn chico" onClick={() => setViendo(null)}>Cerrar</button>}
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Llena fila por fila: <b>Enter</b> pasa a la casilla siguiente. Toca el número del diente para marcarlo ausente; movilidad y furca cambian de 0 a 3 con cada toque. Sondaje de 4 mm o más sale en ámbar y de 6 mm o más en rojo.
            El nivel de inserción (NIC) se calcula solo: sondaje + recesión.
          </p>
          <Arcada dientes={DIENTES_SUPERIORES} datos={mostrado} cambiar={cambiar} bloqueado={!editable} inferior={false} />
          <Arcada dientes={DIENTES_INFERIORES} datos={mostrado} cambiar={cambiar} bloqueado={!editable} inferior />
          <Resumen r={viendo ? viendo.resumen : resumen!} />
          {viendo?.observaciones && <p style={{ margin: 0 }}>{viendo.observaciones}</p>}
          {editable && (
            <>
              <label className="campo">Observaciones (opcional)<textarea rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
              {problemas.length > 0 && <div className="aviso info"><ul>{problemas.map((x) => <li key={x}>{x}</li>)}</ul></div>}
              <AvisoError error={a.error} />
              <div className="fila">
                <button className="btn pri" disabled={a.ocupado || problemas.length > 0} onClick={guardar}>Guardar periodontograma</button>
                <button className="btn" onClick={() => setDatos(null)}>Cancelar</button>
              </div>
              <small className="muted">Al guardarlo queda como registro fijo; si te equivocas, haz uno nuevo.</small>
            </>
          )}
        </>
      )}
      {lista.length > 0 && !datos && (
        <>
          <h3>Registros anteriores</h3>
          <ul className="lista">
            {lista.map((x, i) => {
              const anterior = lista[i + 1];
              const c = anterior ? compararPerio(anterior.resumen, x.resumen) : null;
              return (
                <li key={x.id}>
                  <button onClick={() => setViendo(x)} aria-current={viendo?.id === x.id}>
                    <span><b>{fechaLarga(x.fecha)}</b> · {x.profesional}<br /><span className="muted" style={{ fontSize: 13 }}>{textoResumenPerio(x.resumen)}</span>
                      {c && <><br /><span style={{ fontSize: 13 }}>Frente al anterior: {[signo(c.pdMedia, "mm de sondaje medio"), signo(c.pctSangrado, "puntos de sangrado"), signo(c.sitios4 || null, "sitios ≥ 4 mm")].filter(Boolean).join(" · ") || "sin diferencias"}</span></>}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {!mostrado && <AvisoError error={a.error} />}
    </Seccion>
  );
}
